import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Table, Card, Alert, Badge, Spinner, Form } from 'react-bootstrap';
import { useGetGarminConnectedUsersQuery } from '../slices/garminApiSlice';

// Simple admin diagnostics page (route integration may be needed)
// Requires athlete.isAdmin on backend for protected debug endpoints

const formatTimestamp = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatDistance = (meters) => {
  const numeric = Number(meters);
  if (!numeric) return null;
  return `${(numeric / 1609.34).toFixed(2)} mi`;
};

const renderUserSummary = (user) => {
  const parts = [
    user?.username,
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || null,
    user?.ownTeamName ? `Own Team: ${user.ownTeamName}` : (user?.teamName ? `Own Team: ${user.teamName}` : null),
    user?.draftedTeamName && user?.draftedTeamName !== user?.ownTeamName ? `Drafted By: ${user.draftedTeamName}` : null,
  ].filter(Boolean);

  if (parts.length === 0 && user?.garminUserId) {
    return `Garmin ID: ${user.garminUserId}`;
  }

  return parts.join(' | ') || 'No matched LeagueRunner user';
};

const GarminAdminDiagnostics = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const { data: connectedUsersData, isLoading: loadingConnectedUsers, refetch: refetchConnectedUsers, error: connectedUsersError } = useGetGarminConnectedUsersQuery(undefined, { skip: !userInfo });
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [sample, setSample] = useState(null);
  const [error, setError] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [userIds, setUserIds] = useState([]);
  const [linking, setLinking] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [manualGarminPushUserId, setManualGarminPushUserId] = useState('');
  const [runBackfill, setRunBackfill] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  const apiBase = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
  const buildUrl = (p) => `${apiBase}${p.startsWith('/') ? p : '/' + p}`;

  const fetchLogs = async () => {
    setLoadingLogs(true);
    setError(null);
    try {
      const res = await fetch(buildUrl('/garmin/debug/push-logs?limit=50'), { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLogs(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchSample = async () => {
    try {
      const res = await fetch(buildUrl('/garmin/debug/sample-payload'), { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSample(data.sample);
      }
    } catch {}
  };

  const clearLogs = async () => {
    setClearing(true);
    try {
      const res = await fetch(buildUrl('/garmin/debug/push-logs'), { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setLogs([]);
    } catch (e) {
      setError(e.message);
    } finally {
      setClearing(false);
    }
  };

  const fetchUserIds = async () => {
    try {
      const res = await fetch(buildUrl('/garmin/debug/push-user-ids'), { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUserIds(data.userIds || []);
      }
    } catch {}
  };

  const autoLink = async () => {
    setLinking(true);
    setLinkResult(null);
    try {
      const res = await fetch(buildUrl('/garmin/debug/auto-link-latest'), { method: 'POST', credentials: 'include' });
      const data = await res.json();
      setLinkResult(data);
      if (data.linked) {
        // Refresh logs after linking to see imported counts on next push
        fetchLogs();
      }
    } catch (e) {
      setLinkResult({ error: e.message });
    } finally {
      setLinking(false);
    }
  };

  const assignGarminPushUserId = async () => {
    setAssigning(true);
    setAssignResult(null);
    setError(null);
    try {
      const res = await fetch(buildUrl('/garmin/debug/assign-push-user-id'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: selectedUserId,
          garminPushUserId: manualGarminPushUserId,
          runBackfill,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
      setAssignResult({ success: true, ...data });
      refetchConnectedUsers();
      fetchLogs();
      fetchUserIds();
    } catch (e) {
      setAssignResult({ success: false, message: e.message });
    } finally {
      setAssigning(false);
    }
  };

  const handleUseCandidateId = (candidateId) => {
    setManualGarminPushUserId(candidateId);
  };

  const handleSelectUser = (user) => {
    setSelectedUserId(user._id);
  };

  useEffect(() => {
    fetchLogs();
    fetchSample();
    fetchUserIds();
  }, []);

  return (
    <div className="container py-4">
      <h1 className="mb-3">Garmin Admin Diagnostics</h1>
      <p className="text-muted">Inspect incoming Garmin push payloads, view parsed keys, and compare with a sample.</p>

      <div className="d-flex gap-2 mb-3">
        <Button variant="primary" onClick={fetchLogs} disabled={loadingLogs}>
          {loadingLogs ? <Spinner size="sm" /> : 'Refresh Logs'}
        </Button>
        <Button variant="outline-danger" onClick={clearLogs} disabled={clearing}>
          {clearing ? <Spinner size="sm" /> : 'Clear Logs'}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Header>
          <strong>Recent Push Logs</strong>
        </Card.Header>
        <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {loadingLogs && <p className="text-muted">Loading...</p>}
          {!loadingLogs && logs.length === 0 && <p className="text-muted">No logs found.</p>}
          {logs.length > 0 && (
            <Table size="sm" hover>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Status</th>
                  <th>Imported</th>
                  <th>Users / Team</th>
                  <th>Activity Details</th>
                  <th>Preview (truncated)</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l._id}>
                    <td>{formatTimestamp(l.receivedAt)}</td>
                    <td>
                      <Badge bg={l.status === 'processed' ? 'success' : l.status === 'failed' ? 'danger' : 'warning'}>
                        {l.status || 'received'}
                      </Badge>
                    </td>
                    <td><Badge bg={l.importedCount > 0 ? 'success' : 'secondary'}>{l.importedCount}</Badge></td>
                    <td style={{ maxWidth: '240px', whiteSpace: 'normal' }}>
                      {Array.isArray(l.relatedUsers) && l.relatedUsers.length > 0 ? (
                        l.relatedUsers.map((user, idx) => (
                          <div key={`${l._id}-user-${idx}`} className="small mb-1">
                            {renderUserSummary(user)}
                          </div>
                        ))
                      ) : (
                        <span className="text-muted small">No user linked from this log</span>
                      )}
                    </td>
                    <td style={{ maxWidth: '320px', whiteSpace: 'normal' }}>
                      {Array.isArray(l.activityDetails) && l.activityDetails.length > 0 ? (
                        l.activityDetails.slice(0, 4).map((activity, idx) => (
                          <div key={`${l._id}-activity-${idx}`} className="small mb-1">
                            <strong>{activity.activityType || 'Activity'}</strong>
                            {activity.status ? ` | ${activity.status}` : ''}
                            {activity.startTime ? ` | ${formatTimestamp(activity.startTime)}` : ''}
                            {formatDistance(activity.distanceMeters) ? ` | ${formatDistance(activity.distanceMeters)}` : ''}
                            {activity.reason ? ` | ${activity.reason}` : ''}
                          </div>
                        ))
                      ) : (
                        <div className="small text-muted">{(l.bodyKeys || []).join(', ') || 'No parsed activity details'}</div>
                      )}
                      {Array.isArray(l.activityDetails) && l.activityDetails.length > 4 && (
                        <div className="small text-muted">+{l.activityDetails.length - 4} more</div>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: '360px' }}>{l.bodyPreview}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header><strong>Sample Expected Activity Payload</strong></Card.Header>
        <Card.Body>
          {sample ? (
            <pre style={{ fontSize: '0.75rem' }}>{JSON.stringify(sample, null, 2)}</pre>
          ) : (
            <p className="text-muted">Loading sample...</p>
          )}
          <p className="small text-muted mb-0">Adjust parsing logic in <code>garminRoutes.js</code> if actual keys differ (see push logs above).</p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Garmin-Connected Users</strong>
          <div className="d-flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => refetchConnectedUsers()} disabled={loadingConnectedUsers}>Refresh</Button>
          </div>
        </Card.Header>
        <Card.Body>
          {connectedUsersError && <Alert variant="danger" className="py-2">Failed to load connected users.</Alert>}
          {loadingConnectedUsers && <p className="text-muted">Loading connected users...</p>}
          {!loadingConnectedUsers && connectedUsersData && connectedUsersData.count === 0 && <p className="text-muted">No users have Garmin connected yet.</p>}
          {!loadingConnectedUsers && connectedUsersData && connectedUsersData.count > 0 && (
            <Table size="sm" bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Username</th>
                  <th>User ID</th>
                  <th>Email</th>
                  <th>OAuth Garmin GUID</th>
                  <th>Push / Data Viewer ID</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {connectedUsersData.users.map((u, idx) => (
                  <tr key={u._id}>
                    <td>{idx + 1}</td>
                    <td><code>{u.username}</code></td>
                    <td style={{ fontSize: '0.75rem' }}><code>{u._id}</code></td>
                    <td style={{ fontSize: '0.75rem' }}>{u.email}</td>
                    <td style={{ fontSize: '0.75rem' }}><code>{u.garminUserId}</code></td>
                    <td style={{ fontSize: '0.75rem' }}><code>{u.garminPushUserId || 'unassigned'}</code></td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => handleSelectUser(u)}>
                        Select
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header><strong>Manual Garmin Push ID Mapping</strong></Card.Header>
        <Card.Body>
          <p className="small text-muted">
            Use this when Garmin OAuth stores one ID but webhook/Data Viewer traffic uses a different one. Select a connected user, paste the webhook/Data Viewer ID, and optionally replay stored Garmin pushes.
          </p>
          <Form>
            <div className="row g-3 align-items-end">
              <div className="col-md-5">
                <Form.Group controlId="garminManualUser">
                  <Form.Label>LeagueRunner User</Form.Label>
                  <Form.Select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
                    <option value="">Select a connected user...</option>
                    {(connectedUsersData?.users || []).map((u) => (
                      <option key={u._id} value={u._id}>
                        {u.username} ({u.email})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-5">
                <Form.Group controlId="garminManualPushId">
                  <Form.Label>Garmin Push / Data Viewer ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={manualGarminPushUserId}
                    onChange={(e) => setManualGarminPushUserId(e.target.value.trim())}
                    placeholder="3978d9a6-7da6-4ada-bef5-74a4ba28b351"
                  />
                </Form.Group>
              </div>
              <div className="col-md-2">
                <Button
                  className="w-100"
                  variant="primary"
                  onClick={assignGarminPushUserId}
                  disabled={assigning || !selectedUserId || !manualGarminPushUserId}
                >
                  {assigning ? <Spinner size="sm" /> : 'Assign'}
                </Button>
              </div>
            </div>
            <Form.Check
              className="mt-3"
              type="checkbox"
              id="garminRunBackfill"
              label="Replay stored Garmin push logs after assignment"
              checked={runBackfill}
              onChange={(e) => setRunBackfill(e.target.checked)}
            />
          </Form>
          {userIds.length > 0 && (
            <div className="mt-3">
              <div className="small text-muted mb-2">Recent candidate push IDs:</div>
              <div className="d-flex flex-wrap gap-2">
                {userIds.map((id) => (
                  <Button key={id} size="sm" variant="outline-secondary" onClick={() => handleUseCandidateId(id)}>
                    <code>{id}</code>
                  </Button>
                ))}
              </div>
            </div>
          )}
          {assignResult && (
            <Alert variant={assignResult.success ? 'success' : 'danger'} className="mt-3 mb-0 py-2">
              {assignResult.success ? (
                <span>
                  Assigned <code>{assignResult.user?.garminPushUserId}</code> to <strong>{assignResult.user?.username}</strong>
                  {typeof assignResult.backfilled === 'number' ? ` and backfilled ${assignResult.backfilled} activities` : ''}.
                </span>
              ) : (
                <span>{assignResult.message}</span>
              )}
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header><strong>Garmin User ID Linking</strong></Card.Header>
        <Card.Body>
          <p className="small text-muted">We must link your Garmin <code>userId</code> to import activities. This action is explicit and only succeeds when there is exactly one safe candidate.</p>
          <div className="d-flex gap-2 mb-2">
            <Button variant="secondary" size="sm" onClick={fetchUserIds}>Refresh User IDs</Button>
            <Button variant="primary" size="sm" disabled={linking} onClick={autoLink}>
              {linking ? <Spinner size="sm" /> : 'Link Latest Safe ID'}
            </Button>
          </div>
          {userIds.length === 0 && <p className="text-muted mb-1">No userIds found yet. Generate a new activity and sync Garmin.</p>}
          {userIds.length > 0 && (
            <ul className="small mb-2">
              {userIds.map(id => <li key={id}><code>{id}</code></li>)}
            </ul>
          )}
          {linkResult && (
            <Alert variant={linkResult.linked ? 'success' : 'info'} className="py-2 mb-0">
              {linkResult.error && <span>Error: {linkResult.error}</span>}
              {!linkResult.error && JSON.stringify(linkResult)}
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header><strong>Troubleshooting Checklist</strong></Card.Header>
        <Card.Body>
          <ul className="small">
            <li>Portal endpoints (Activities / Activity Details) set to <strong>push</strong>.</li>
            <li>Check that the OAuth Garmin GUID is stored and compare it with the webhook/Data Viewer ID.</li>
            <li>If webhook/Data Viewer ID differs, assign <code>garminPushUserId</code> from this screen.</li>
            <li>Confirm logs appear after a new activity or using a resender/backfill in Garmin portal.</li>
            <li>If logs show importedCount 0 and bodyKeys unexpected, send one log entry here for parser update.</li>
            <li>Re-run OAuth connect if token may have expired.</li>
          </ul>
        </Card.Body>
      </Card>

      <Alert variant="info">
        <strong>Next Step:</strong> Perform a new activity, sync Garmin, then click "Refresh Logs". Provide the first log row if still not importing.
      </Alert>
    </div>
  );
};

export default GarminAdminDiagnostics;
